import { normalizeRegion, normalizeRole } from './assignmentRules';
import { ALL_REGION_VALUE, ROLE_LABELS } from '../constants/escalateQueryDialog';

/**
 * Sanitize region value to standard format
 * @param {string} region - Region value to sanitize
 * @returns {string} Sanitized region value
 */
export const sanitizeRegionValue = (region) => {
  const normalized = normalizeRegion(region);
  if (!normalized || normalized === 'ALL REGIONS') {
    return ALL_REGION_VALUE;
  }
  return normalized;
};

/**
 * Get user display name from user object
 * @param {Object} user - User object
 * @returns {string} Display name
 */
export const getUserDisplayName = (user = {}) => {
  if (user.name) return user.name;
  if (user.fullName) return user.fullName;
  const parts = [user.firstName, user.lastName].filter(Boolean);
  if (parts.length) return parts.join(' ');
  if (user.email) return user.email;
  return 'Unknown User';
};

/**
 * Resolve default region from current user and query
 * @param {Object} currentUser - Current user object
 * @param {Object} query - Query object
 * @returns {string} Default region value
 */
export const resolveDefaultRegion = (currentUser, query) => {
  const regionSources = [
    sanitizeRegionValue(currentUser?.region),
    sanitizeRegionValue(query?.region),
  ];

  const resolved = regionSources.find((value) => value && value !== '');
  return resolved || ALL_REGION_VALUE;
};

/**
 * Build filtered user options based on role and region
 * @param {Array} users - Array of user objects
 * @param {string} targetRole - Target role to filter by
 * @param {string} selectedRegion - Selected region to filter by
 * @returns {Array} Filtered and sorted user options
 */
export const buildFilteredUserOptions = (users, targetRole, selectedRegion) => {
  const normalizedRole = normalizeRole(targetRole);
  const normalizedRegion = sanitizeRegionValue(selectedRegion);

  return users
    .filter((user) => {
      if (!user) return false;
      const userRole = normalizeRole(user.role);
      if (userRole !== normalizedRole) return false;
      if (user.canBeAssignee === false && user.canBeAssignor === false) return false;
      if (user.isActive === false) return false;

      if (!normalizedRegion || normalizedRegion === 'ALL') {
        return true;
      }

      const userRegion = normalizeRegion(user.region);
      if (!userRegion || userRegion === 'ALL') {
        return true;
      }

      return userRegion === normalizedRegion;
    })
    .sort((a, b) =>
      getUserDisplayName(a).localeCompare(getUserDisplayName(b), undefined, { sensitivity: 'base' })
    );
};

/**
 * Validate escalation form
 * @param {string} selectedRole - Selected role
 * @param {string} selectedUserId - Selected user ID
 * @param {Array} fields - Available fields
 * @returns {Object} Validation result with isValid flag and errors object
 */
export const validateEscalationForm = (selectedRole, selectedUserId, fields) => {
  const errors = {};

  if (!selectedRole) {
    errors.role = 'Please choose a role';
  }

  if (!selectedUserId) {
    errors.target = 'Please choose a recipient';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Build escalation payload for submission
 * @param {string} selectedUserId - Selected user ID
 * @param {Map} usersById - Map of users by ID
 * @param {string} selectedRole - Selected role
 * @param {string} region - Selected region
 * @returns {Object} Escalation payload
 */
export const buildEscalationPayload = (selectedUserId, usersById, selectedRole, region) => {
  const targetKey = selectedUserId?.toString();
  const targetUser =
    usersById.get(targetKey) ||
    usersById.get(selectedUserId) ||
    usersById.get(selectedUserId?.toString()) ||
    null;

  if (!targetUser) {
    return null;
  }

  const targetRole = normalizeRole(targetUser?.role);
  const normalizedSelectedRegion = sanitizeRegionValue(region);

  return {
    selectedRegion: normalizedSelectedRegion,
    escalationPath: [
      {
        role: targetRole,
        roleLabel: ROLE_LABELS[targetRole] || targetRole,
        userId: targetUser?._id || targetUser?.id || selectedUserId,
        userName: getUserDisplayName(targetUser),
        userRegion: normalizeRegion(targetUser?.region) || 'ALL',
      },
    ],
    primaryTargetId: targetUser?._id || targetUser?.id || selectedUserId || null,
  };
};
